import React, { useEffect, useId, useRef } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';

import { Form, Select } from 'antd';
import NumericInputField from 'components/common/NumericInputField';
import PillSegmented, {
  type PillSegmentedOption,
} from 'components/common/PillSegmented/PillSegmented';
import {
  SEED_TARGET_PICKER_TITLE,
  SEED_TREATMENT_APPLY_LABEL,
  SEED_TREATMENT_COST_HINT,
  SEED_TREATMENT_COST_LABEL,
  SEED_TREATMENT_RATE_HINT_PER_100KG,
  SEED_TREATMENT_RATE_HINT_PER_HA,
  SEED_TREATMENT_RATE_LABEL,
  SEED_TREATMENT_RATE_MODE_PER_100KG_LABEL,
  SEED_TREATMENT_RATE_MODE_PER_HA_LABEL,
  SEED_TREATMENT_UNIT_LABEL,
  TASKS_GRID_SELECT_UNIT_PLACEHOLDER,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';
import { InputStepType, SeedDressRateMode } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import MergeClassNames from 'utils/MergeClassNames';

import SeedTargetList from './SeedTargetList';
import {
  getLinkedSeedStepOptions,
  getRateUnitOptions,
} from './stepCalculations';
import {
  SEED_TREATMENT_FIELDS_ATTRIBUTE,
  STEP_PICKER_FLYOUT_SELECTOR,
  StepSelectorOption,
} from './stepConfig';

interface SeedTreatmentFieldsProps {
  task: IInputTask;
  gUnits: GUnit[];
  rateMode: SeedDressRateMode;
  rate: number | null;
  unit: IUnit | null;
  cost: number | null;
  linkedStepId: string | null;
  checkedSeeds?: StepSelectorOption[];
  autoSelectOnMount?: boolean;
  className?: string;
  listClassName?: string;
  onRateModeChange: (mode: SeedDressRateMode) => void;
  onRateChange: (rate: number | null) => void;
  onUnitChange: (unit: IUnit | null) => void;
  onCostChange: (cost: number | null) => void;
  onTargetSelect: (stepId: string | null) => void;
}

const FIELD_WIDTH_CLASSNAME = '!w-40';

const RATE_MODE_OPTIONS: PillSegmentedOption<SeedDressRateMode>[] = [
  {
    label: SEED_TREATMENT_RATE_MODE_PER_HA_LABEL,
    value: SeedDressRateMode.PerHa,
  },
  {
    label: SEED_TREATMENT_RATE_MODE_PER_100KG_LABEL,
    value: SeedDressRateMode.Per100Kg,
  },
];

/**
 * Enter in a number field hands focus back so a second Enter adds / saves the draft:
 * to the flyout box in the add flow, or simply off the field in the edit two-panel.
 */
const releaseFocusOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.key !== 'Enter') return;
  event.preventDefault();
  const input = event.currentTarget;
  const flyout = input.closest<HTMLElement>(STEP_PICKER_FLYOUT_SELECTOR);
  if (flyout) flyout.focus();
  else input.blur();
};

const renderFieldLabel = (label: string, hint?: string) => (
  <span
    className={MergeClassNames(
      FIELD_WIDTH_CLASSNAME,
      'flex flex-row justify-between text-sm text-charcoal'
    )}
  >
    <span>{label}</span>
    {hint && <span className="text-gray-muted">{hint}</span>}
  </span>
);

/**
 * Seed Treatment rate mode, Rate/Unit, Cost and Seed treated fields, mirroring mobile.
 * Shared by SeedTreatmentFlyout (add) and SeedTreatmentTwoPanel (edit).
 */
const SeedTreatmentFields: React.FC<SeedTreatmentFieldsProps> = ({
  task,
  gUnits,
  rateMode,
  rate,
  unit,
  cost,
  linkedStepId,
  checkedSeeds,
  autoSelectOnMount = true,
  className,
  listClassName,
  onRateModeChange,
  onRateChange,
  onUnitChange,
  onCostChange,
  onTargetSelect,
}) => {
  const fieldIdPrefix = useId();
  const rateInputId = `${fieldIdPrefix}-rate`;
  const unitInputId = `${fieldIdPrefix}-unit`;
  const costInputId = `${fieldIdPrefix}-cost`;

  // Stable ref so the auto-select effect calls the latest callback without re-running.
  const onTargetSelectRef = useRef(onTargetSelect);
  onTargetSelectRef.current = onTargetSelect;

  const isFirstAutoSelectRunRef = useRef(true);

  // Auto-select the first seed on entering Per100Kg; rateMode-only so a manual deselect sticks.
  useEffect(() => {
    const isFirstRun = isFirstAutoSelectRunRef.current;
    isFirstAutoSelectRunRef.current = false;
    if (isFirstRun && !autoSelectOnMount) return;
    if (rateMode !== SeedDressRateMode.Per100Kg) return;
    if (linkedStepId !== null) return;
    const opts = getLinkedSeedStepOptions(task, checkedSeeds);
    if (opts.length > 0) {
      onTargetSelectRef.current(opts[0].value);
    }
    // linkedStepId/task/checkedSeeds intentionally omitted: only re-run on rateMode change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateMode]);

  const hasNoTargetOptions =
    rateMode === SeedDressRateMode.Per100Kg &&
    getLinkedSeedStepOptions(task, checkedSeeds).length === 0;

  const unitOptions = getRateUnitOptions(InputStepType.SeedDress, gUnits);
  // Unmatched units (e.g. dummy catalogue IDs) show the placeholder, not a raw ID.
  const selectedUnitId = unitOptions.some(
    (option) => option.value === unit?.globalId
  )
    ? unit?.globalId
    : undefined;
  const rateHint =
    rateMode === SeedDressRateMode.PerHa
      ? SEED_TREATMENT_RATE_HINT_PER_HA
      : SEED_TREATMENT_RATE_HINT_PER_100KG;

  const handleUnitChange = (globalId: string | undefined) => {
    const selected = gUnits.find((u) => u.globalId === globalId);
    onUnitChange(
      selected ? { unit: selected.code, globalId: selected.globalId } : null
    );
  };

  return (
    <div
      {...{ [SEED_TREATMENT_FIELDS_ATTRIBUTE]: '' }}
      className={MergeClassNames('flex flex-col gap-2', className)}
    >
      <Form.Item
        className="!mb-0 shrink-0"
        label={
          <span className="text-sm text-charcoal">
            {SEED_TREATMENT_APPLY_LABEL}
          </span>
        }
        colon={false}
        layout="vertical"
      >
        <PillSegmented<SeedDressRateMode>
          options={RATE_MODE_OPTIONS}
          value={rateMode}
          onChange={onRateModeChange}
        />
      </Form.Item>

      <div className="flex shrink-0 flex-wrap gap-x-3 gap-y-2">
        <Form.Item
          className="!mb-0"
          layout="vertical"
          colon={false}
          htmlFor={rateInputId}
          label={renderFieldLabel(SEED_TREATMENT_RATE_LABEL, rateHint)}
        >
          <NumericInputField
            id={rateInputId}
            size="middle"
            controls={false}
            keyboard={false}
            min={0}
            inputClassName={FIELD_WIDTH_CLASSNAME}
            value={rate}
            onChange={onRateChange}
            onKeyDown={releaseFocusOnEnter}
          />
        </Form.Item>
        <Form.Item
          className="!mb-0"
          layout="vertical"
          colon={false}
          htmlFor={unitInputId}
          label={renderFieldLabel(SEED_TREATMENT_UNIT_LABEL)}
        >
          <Select
            id={unitInputId}
            className={FIELD_WIDTH_CLASSNAME}
            getPopupContainer={() => document.body}
            placeholder={TASKS_GRID_SELECT_UNIT_PLACEHOLDER}
            allowClear
            suffixIcon={<IoMdArrowDropdown className="text-gray-dark" />}
            options={unitOptions}
            value={selectedUnitId}
            onChange={handleUnitChange}
          />
        </Form.Item>
      </div>

      <Form.Item
        className="!mb-0 shrink-0"
        layout="vertical"
        colon={false}
        htmlFor={costInputId}
        label={renderFieldLabel(
          SEED_TREATMENT_COST_LABEL,
          SEED_TREATMENT_COST_HINT
        )}
      >
        <NumericInputField
          id={costInputId}
          size="middle"
          controls={false}
          keyboard={false}
          min={0}
          inputClassName={FIELD_WIDTH_CLASSNAME}
          value={cost}
          onChange={onCostChange}
          onKeyDown={releaseFocusOnEnter}
        />
      </Form.Item>

      {rateMode === SeedDressRateMode.Per100Kg && (
        <div className="flex flex-1 min-h-0 flex-col">
          {/* Plain span, not Form.Item, so the list keeps its flex scroll. */}
          <span
            className={MergeClassNames(
              'shrink-0 pb-2 text-sm',
              hasNoTargetOptions ? 'text-danger-dark' : 'text-charcoal'
            )}
          >
            {SEED_TARGET_PICKER_TITLE}
          </span>
          <SeedTargetList
            task={task}
            highlightedStepId={linkedStepId}
            checkedSeeds={checkedSeeds}
            onSelect={onTargetSelect}
            className={MergeClassNames('min-h-0 max-h-none', listClassName)}
          />
        </div>
      )}
    </div>
  );
};

export type { SeedTreatmentFieldsProps };
export default SeedTreatmentFields;
